import React from 'react';

/**
 * Interface pour les propriétés de ErrorBoundary.
 */
interface ErrorBoundaryProps {
  children: React.ReactNode;
}

/**
 * Interface pour l'état de ErrorBoundary.
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary capture les erreurs de rendu pour éviter les écrans blancs.
 * Implémentation compatible React 18/19 avec typage strict.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  // Initialisation de l'état sans constructeur (plus propre en TS)
  public state: ErrorBoundaryState = {
    hasError: false,
    error: null,
  };

  /**
   * Met à jour l'état suite à une erreur pour afficher l'UI de secours.
   */
  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  /**
   * Log l'erreur pour le débogage.
   */
  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[CRITICAL] Erreur UI détectée:', error);
    console.groupCollapsed('Détails techniques de l\'erreur');
    console.error(errorInfo);
    console.groupEnd();
  }

  /**
   * Réinitialise l'application.
   */
  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  public render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6 font-sans">
          <div className="max-w-md w-full bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl text-center space-y-6">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl" role="img" aria-label="danger">⚠️</span>
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-white">Une erreur est survenue</h1>
              <p className="text-slate-400 text-sm">
                L'application a rencontré un problème inattendu. Vos données sont préservées.
              </p>
            </div>

            <div className="p-4 bg-black/40 rounded-xl text-left border border-white/5">
              <code className="text-[10px] text-red-400/80 break-all">
                {this.state.error?.message || "Erreur inconnue"}
              </code>
            </div>

            <button
              onClick={this.handleReset}
              className="w-full py-4 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white font-bold rounded-2xl transition-all shadow-md active:scale-95"
            >
              Relancer l'application
            </button>
            
            <p className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">
              Sécurisation Active • Carnet de Mesure
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
